import { useMemo, useState } from 'react';
import { Button, Group, Modal, Radio, Text, TextInput } from '@mantine/core';
import { useIntl } from 'react-intl';

import { insuranceModalMessages } from './insurance-modal-messages';
import styles from './InsuranceModal.module.css';
import { type InsuranceCommodityCategory, InsuranceItemCondition } from '../../../../types/freight-requests';
import { CustomNumberInput } from '../../../common/custom-number-input';
import { Dropdown } from '../../../common/Dropdown';
import { freightFormMessages } from '../../../freight-browser/components/freight-form/freight-form-messages';
import { ModalContent } from '../../../modal-content';
import { ModalFooter } from '../../../modal-footer';

import type { WizardInsurance } from '../../freight-booking-wizard-types';

const CATEGORY_VALUES: InsuranceCommodityCategory[] = [
  'GeneralMerchandise',
  'Equipment',
  'FoodBeverages',
  'ChemicalsPaint',
  'Electronics',
  'Construction',
  'Furniture',
  'Stonework',
  'FragileItems',
  'Perishables',
  'Jewelry',
  'MotorizedTransport',
  'ArtAntiques',
  'Explosives',
  'LifeForms',
  'ControlledItems',
  'PharmaNonRefrigerated',
  'PharmaRefrigerated',
];

const CATEGORY_MESSAGE_MAP: Record<InsuranceCommodityCategory, keyof typeof freightFormMessages> = {
  GeneralMerchandise: 'categoryGeneralMerchandise',
  Equipment: 'categoryEquipment',
  FoodBeverages: 'categoryFoodBeverages',
  ChemicalsPaint: 'categoryChemicalsPaint',
  Electronics: 'categoryElectronics',
  Construction: 'categoryConstruction',
  Furniture: 'categoryFurniture',
  Stonework: 'categoryStonework',
  FragileItems: 'categoryFragileItems',
  Perishables: 'categoryPerishables',
  Jewelry: 'categoryJewelry',
  MotorizedTransport: 'categoryMotorizedTransport',
  ArtAntiques: 'categoryArtAntiques',
  Explosives: 'categoryExplosives',
  LifeForms: 'categoryLifeForms',
  ControlledItems: 'categoryControlledItems',
  PharmaNonRefrigerated: 'categoryPharmaNonRefrigerated',
  PharmaRefrigerated: 'categoryPharmaRefrigerated',
};

type InsuranceModalProps = {
  insurance: WizardInsurance;
  opened: boolean;
  onClose: () => void;
  onSave: (next: WizardInsurance) => void;
  zIndex?: number;
};

type InsuranceModalContentProps = {
  insurance: WizardInsurance;
  onClose: () => void;
  onSave: (next: WizardInsurance) => void;
};

const InsuranceModalContent = ({ insurance, onSave, onClose }: InsuranceModalContentProps) => {
  const intl = useIntl();
  const [conditionOfGoods, setConditionOfGoods] = useState<InsuranceItemCondition>(
    (insurance.conditionOfGoods as InsuranceItemCondition) || InsuranceItemCondition.New,
  );
  const [category, setCategory] = useState(insurance.category);
  const [insuredValue, setInsuredValue] = useState(insurance.insuredValue);
  const [marksAndNumbers, setMarksAndNumbers] = useState(insurance.marksAndNumbers);

  const categoryOptions = useMemo(
    () => CATEGORY_VALUES.map((v) => ({ value: v, label: intl.formatMessage(freightFormMessages[CATEGORY_MESSAGE_MAP[v]]) })),
    [intl],
  );

  const handleSave = () => {
    onSave({ ...insurance, conditionOfGoods, category, insuredValue, marksAndNumbers });
    onClose();
  };
  return (
    <>
      <ModalContent>
        <Text size='sm'>{intl.formatMessage(insuranceModalMessages.description)}</Text>
        <Radio.Group
          data-testid='insurance-condition'
          label={intl.formatMessage(insuranceModalMessages.conditionOfGoodsLabel)}
          onChange={(val) => setConditionOfGoods(val as InsuranceItemCondition)}
          value={conditionOfGoods}
          withAsterisk
        >
          <Group gap='md' mt='xs'>
            <Radio label={intl.formatMessage(freightFormMessages.conditionNew)} value='New' />
            <Radio label={intl.formatMessage(freightFormMessages.conditionUsed)} value='Used' />
          </Group>
        </Radio.Group>
        <div className={styles.fieldsGrid}>
          <Dropdown
            data={categoryOptions}
            data-testid='insurance-category'
            label={intl.formatMessage(freightFormMessages.categoryLabel)}
            onChange={(val) => setCategory(val ?? '')}
            placeholder={intl.formatMessage(freightFormMessages.selectOnePlaceholder)}
            value={category || null}
            withAsterisk
          />
          <CustomNumberInput
            allowNegative={false}
            data-testid='insurance-value'
            label={intl.formatMessage(freightFormMessages.insuredValueLabel)}
            min={0}
            onChange={(val) => setInsuredValue(typeof val === 'string' ? parseFloat(val) || 0 : val)}
            prefix='$ '
            value={insuredValue}
            withAsterisk
          />
          <TextInput
            data-testid='insurance-marks'
            label={intl.formatMessage(insuranceModalMessages.marksAndNumbersLabel)}
            onChange={(e) => setMarksAndNumbers(e.currentTarget.value)}
            placeholder={intl.formatMessage(insuranceModalMessages.marksAndNumbersPlaceholder)}
            value={marksAndNumbers}
          />
        </div>
      </ModalContent>
      <ModalFooter>
        <Button data-testid='insurance-cancel' onClick={onClose} variant='tertiary'>
          {intl.formatMessage(insuranceModalMessages.cancel)}
        </Button>
        <Button data-testid='insurance-save' onClick={handleSave} variant='primary'>
          {intl.formatMessage(insuranceModalMessages.save)}
        </Button>
      </ModalFooter>
    </>
  );
};

export const InsuranceModal = ({ insurance, opened, onClose, onSave, zIndex }: InsuranceModalProps) => {
  const intl = useIntl();

  return (
    <Modal onClose={onClose} opened={opened} size='sm' title={intl.formatMessage(insuranceModalMessages.title)} zIndex={zIndex}>
      <InsuranceModalContent insurance={insurance} onClose={onClose} onSave={onSave} />
    </Modal>
  );
};
